import {
  Screen,
  LayoutType,
  LayoutStyle,
  TextStyle,
  ContainerStyle,
  PriceBoxStyle,
  PanfletoContentConfig,
} from '../../../shared/types';
import {
  defaultPanfletoContent,
  mergePanfletoContent,
  mergePanfletoContentPreserving,
  overlayPanfletoContentKeepingProductNameAlign,
  readPanfletoContentFromConfigObject,
} from '../utils/panfletoContentUtils';
import { bannerService, TelaBanner, InserirTelaBannerCommand, AlterarTelaBannerCommand } from './bannerService';

/** Re-export para SignalR/Display: mesmo merge de panfleto que o editor. */
export {
  mergePanfletoContent,
  mergePanfletoContentPreserving,
  overlayPanfletoContentKeepingProductNameAlign,
} from '../utils/panfletoContentUtils';

/**
 * Serviço adaptador entre a API de Banner do Market Gestor e o sistema local de telas
 * 
 * Este serviço faz a ponte entre:
 * - API Externa: /v1/banner/tela (Market Gestor)
 * - Sistema Local: Store Zustand com telas locais
 */

// Mapeamento entre layouts locais e tipos da API
const LAYOUT_MAPPING: Record<LayoutType, string> = {
    'simple2col': 'Lista com Colunas',
    'panfleto': 'Panfleto',
    'video': 'Vídeo'
};

// Mapeamento entre layouts locais e tipos numéricos da API
export const LAYOUT_TO_TIPO: Record<LayoutType, number> = {
    'simple2col': 0,
    'panfleto': 1,
    'video': 2
};


export const TIPO_TO_LAYOUT: Record<number, LayoutType> = {
    0: 'simple2col',
    1: 'panfleto',
    2: 'video'
};

const baseText = (): TextStyle => ({
    color: '#000000',
    fontSize: 16,
    fontFamily: 'Arial, sans-serif',
    lineHeight: 1.2,
    letterSpacing: 0,
    fontWeight: 400,
    verticalStretch: 100,
    showCurrency: true,
});

const baseContainer = (): ContainerStyle => ({
    backgroundColor: '#ffffff',
    useGradient: false,
    gradientStart: '#ffffff',
    gradientEnd: '#f0f0f0',
    borderRadius: 8,
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    horizontalGap: 8,
    verticalGap: 8,
    backgroundVignetteEnabled: false,
    backgroundVignetteIntensity: 35,
});

const basePriceBox = (): PriceBoxStyle => ({
    enabled: false,
    backgroundColor: '#ffffff',
    borderColor: '#000000',
    borderWidth: 0,
    borderRadius: 8,
    paddingTop: 4,
    paddingRight: 8,
    paddingBottom: 4,
    paddingLeft: 8,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
});

/** Estilo inicial completo por tipo de tela (usado ao trocar tipo ou criar config vazia). */
export function buildDefaultLayoutStyleForLayout(layoutId: LayoutType): LayoutStyle {
    const bt = baseText();
    const bc = baseContainer();
    const pb = basePriceBox();

    switch (layoutId) {
        case 'panfleto':
            return {
                productName: {
                    ...bt,
                    fontSize: 36,
                    color: '#111111',
                    fontFamily: "'Oswald', 'Arial Narrow', sans-serif",
                },
                price: {
                    ...bt,
                    fontSize: 56,
                    color: '#cc0000',
                    fontFamily: "'Bebas Neue', Impact, sans-serif",
                },
                unit: {
                    ...bt,
                    fontSize: 20,
                    color: '#666666',
                    fontFamily: "'Oswald', 'Arial Narrow', sans-serif",
                },
                description: {
                    ...bt,
                    fontSize: 14,
                    color: '#2563eb',
                    fontFamily: "'Oswald', 'Arial Narrow', sans-serif",
                },
                category: {
                    ...bt,
                    fontSize: 42,
                    color: '#111111',
                    fontWeight: 800,
                    fontFamily: "'Oswald', 'Arial Narrow', sans-serif",
                },
                validUntil: {
                    ...bt,
                    fontSize: 14,
                    color: '#cc0000',
                    fontFamily: "'Oswald', 'Arial Narrow', sans-serif",
                },
                productContainer: { ...bc },
                priceBox: { ...pb },
                panfletoContent: defaultPanfletoContent(),
            };
        case 'video':
            return {
                productName: { ...bt, fontSize: 24, color: '#333333' },
                price: { ...bt, fontSize: 32, color: '#006400' },
                unit: { ...bt, fontSize: 18, color: '#666666' },
                description: { ...bt, fontSize: 16, color: '#666666' },
                category: { ...bt, fontSize: 14, color: '#666666' },
                validUntil: { ...bt, fontSize: 14, color: '#cc0000' },
                productContainer: { ...bc },
                priceBox: { ...pb },
            };
        case 'simple2col':
        default:
            return {
                productName: { ...bt, fontSize: 24, color: '#333333' },
                price: { ...bt, fontSize: 32, color: '#006400' },
                unit: { ...bt, fontSize: 18, color: '#666666' },
                description: { ...bt, fontSize: 16, color: '#666666' },
                category: { ...bt, fontSize: 14, color: '#666666' },
                validUntil: { ...bt, fontSize: 14, color: '#cc0000' },
                productContainer: { ...bc },
                priceBox: { ...pb },
            };
    }
}

/** JSON de configuracoes inicial ao mudar o tipo da tela (substitui o anterior). */
export function criarConfiguracoesDefaultParaTipo(layoutId: LayoutType): string | undefined {
    let json = layoutStyleToJson(buildDefaultLayoutStyleForLayout(layoutId));
    if (layoutId === 'panfleto' && json) {
        json = adicionarTempoPanfletoProdutoAoJson(json, 10) ?? json;
    }
    return json;
}

/**
 * Chaves de propriedade da aba Estilo em `ds_configuracoes`.
 * Qualquer outra chave (exibição, sequência, meta, loop, etc.) NÃO deve ser
 * reescrita por saves de estilo — apenas mantida.
 */
export const CONFIG_STYLE_OWNED_KEYS = [
    'productName',
    'price',
    'unit',
    'productContainer',
    'description',
    'category',
    'validUntil',
    'priceBox',
    'panfletoContent',
] as const;

/** Margens salvas pelo menu de exibição (`adicionarMargensExternasAoJson`); save de estilo não sobrescreve. */
const EXTERNAL_PRODUCT_CONTAINER_MARGIN_KEYS = [
    'marginTop',
    'marginRight',
    'marginBottom',
    'marginLeft',
] as const;

function isEmptyConfigJson(configuracoesJson: string | undefined): boolean {
    if (!configuracoesJson) return true;
    const t = configuracoesJson.trim();
    return t === '' || t === 'string' || t === '"string"';
}

/**
 * Parse para funções `adicionar*AoJson`.
 * Se o JSON existente não puder ser lido, aborta o patch e devolve o original intacto.
 */
export function parseConfigForPatch(
    configuracoesJson: string | undefined
): { ok: true; config: Record<string, unknown> } | { ok: false; original: string | undefined } {
    if (isEmptyConfigJson(configuracoesJson)) {
        return { ok: true, config: {} };
    }
    try {
        const parsed = JSON.parse(configuracoesJson!);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return { ok: true, config: { ...(parsed as Record<string, unknown>) } };
        }
        return { ok: false, original: configuracoesJson };
    } catch {
        return { ok: false, original: configuracoesJson };
    }
}

function overlayProductContainerForStyleSave(
    previous: unknown,
    incoming: unknown
): Record<string, unknown> {
    const prevPc =
        previous && typeof previous === 'object'
            ? ({ ...(previous as object) } as Record<string, unknown>)
            : {};
    const incomingPc =
        incoming && typeof incoming === 'object'
            ? (incoming as Record<string, unknown>)
            : {};
    const merged: Record<string, unknown> = { ...prevPc, ...incomingPc };
    for (const key of EXTERNAL_PRODUCT_CONTAINER_MARGIN_KEYS) {
        if (prevPc[key] !== undefined) {
            merged[key] = prevPc[key];
        }
    }
    return merged;
}

/**
 * Antes do PUT de estilo: JSON fresco da API + rascunho do editor.
 * Margens externas vêm da API (menu de exibição); panfleto mescla campo a campo.
 */
export function mergeLayoutStyleForStyleSave(
    freshFromApi: LayoutStyle | undefined,
    editor: LayoutStyle
): LayoutStyle {
    let out: LayoutStyle = { ...editor };

    if (freshFromApi?.productContainer || out.productContainer) {
        out = {
            ...out,
            productContainer: overlayProductContainerForStyleSave(
                freshFromApi?.productContainer,
                out.productContainer
            ) as LayoutStyle['productContainer'],
        };
    }

    if (out.panfletoContent !== undefined || freshFromApi?.panfletoContent) {
        out = {
            ...out,
            panfletoContent: mergePanfletoContentPreserving(
                freshFromApi?.panfletoContent,
                out.panfletoContent
            ),
        };
    }

    return out;
}

function parseConfigObjectSafe(configuracoesJson: string | undefined): Record<string, unknown> {
    if (
        !configuracoesJson ||
        configuracoesJson.trim() === '' ||
        configuracoesJson.trim() === 'string' ||
        configuracoesJson.trim() === '"string"'
    ) {
        return {};
    }
    try {
        const parsed = JSON.parse(configuracoesJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return { ...(parsed as Record<string, unknown>) };
        }
    } catch {
        // ignore
    }
    return {};
}

/**
 * Aplica SOMENTE o bloco de estilo sobre o JSON existente da API.
 * Contrato: JSON fresco + overlay das chaves de estilo; todo o resto intacto
 * (posição, zoom, colunas, sequências, descricao, tag, loop, etc.).
 */
export function aplicarEstiloSobreConfiguracoesExistentes(
    configuracoesJson: string | undefined,
    layoutStyle: LayoutStyle
): string | undefined {
    const styleJson = layoutStyleToJson(layoutStyle);
    if (!styleJson) {
        return configuracoesJson;
    }

    let styleObj: Record<string, unknown>;
    try {
        styleObj = JSON.parse(styleJson) as Record<string, unknown>;
    } catch {
        return configuracoesJson;
    }

    const parsedBase = parseConfigForPatch(configuracoesJson);
    if (!parsedBase.ok) {
        return parsedBase.original;
    }
    const base = parsedBase.config;
    for (const key of CONFIG_STYLE_OWNED_KEYS) {
        if (styleObj[key] !== undefined) {
            if (key === 'panfletoContent') {
                const prevPc = readPanfletoContentFromConfigObject(base);
                const incomingPc = styleObj.panfletoContent as PanfletoContentConfig;
                base.panfletoContent = overlayPanfletoContentKeepingProductNameAlign(
                    prevPc,
                    incomingPc
                );
            } else if (key === 'productContainer') {
                base.productContainer = overlayProductContainerForStyleSave(
                    base.productContainer,
                    styleObj.productContainer
                );
            } else {
                base[key] = styleObj[key];
            }
        }
    }
    return JSON.stringify(base);
}

/**
 * Atualiza apenas as margens externas de `productContainer` (menu de exibição).
 * Não reescreve demais campos de estilo nem outras chaves do JSON.
 */
export function adicionarMargensExternasAoJson(
    configuracoesJson: string | undefined,
    margins: {
        marginTop: number;
        marginRight: number;
        marginBottom: number;
        marginLeft: number;
    }
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    try {
        const config = parsed.config as Record<string, any>;
        const prev =
            config.productContainer && typeof config.productContainer === 'object'
                ? { ...config.productContainer }
                : {};
        config.productContainer = {
            ...prev,
            marginTop: margins.marginTop,
            marginRight: margins.marginRight,
            marginBottom: margins.marginBottom,
            marginLeft: margins.marginLeft,
        };
        return JSON.stringify(config);
    } catch {
        return configuracoesJson;
    }
}

/**
 * Junta o layout vindo da API/SignalR com o que já estava em memória, preservando
 * `panfletoContent` (ex.: cor de fundo) quando a atualização vem parcial.
 */
export function mergeLayoutStyleOnScreenUpdate(
    previous: LayoutStyle | undefined,
    incoming: LayoutStyle | undefined
): LayoutStyle | undefined {
    if (!incoming) {
        return previous ? { ...previous } : undefined;
    }
    if (!previous) {
        return {
            ...incoming,
            panfletoContent: mergePanfletoContent(incoming.panfletoContent as any),
        } as LayoutStyle;
    }
    return {
        ...previous,
        ...incoming,
        priceBox: incoming.priceBox ? incoming.priceBox : previous.priceBox,
        panfletoContent: mergePanfletoContentPreserving(
            previous.panfletoContent,
            incoming.panfletoContent
        ),
    } as LayoutStyle;
}

// Cache só para jsonToLayoutStyle (mesma string da API).
// layoutStyleToJson NÃO usa cache: o antigo LAYOUT_TO_JSON + hash perdia colisão e o PUT ia com JSON errado
// (ex.: `panfletoContent.backgroundColor` de outro estado / valor fixo).
const JSON_TO_LAYOUT_CACHE = new Map<string, LayoutStyle>();
const JSON_TO_LAYOUT_CACHE_BUST = 'v5-panfleto-element-placements';
const MAX_CACHE_SIZE = 200;

// Limpar cache quando exceder o limite - OTIMIZADO
function trimCache(cache: Map<any, any>) {
    if (cache.size > MAX_CACHE_SIZE) {
        // Remover 30% das entradas mais antigas (FIFO) - mais agressivo
        const entriesToRemove = Math.floor(MAX_CACHE_SIZE * 0.3);
        const keys = Array.from(cache.keys());
        for (let i = 0; i < entriesToRemove; i++) {
            cache.delete(keys[i]);
        }
    }
}

/**
 * Converte LayoutStyle para JSON string formatado para salvar em configuracoes
 * Sempre serializa de forma determinística (sem cache) para o PUT nunca reutilizar JSON obsoleto.
 */
export function layoutStyleToJson(layoutStyle: LayoutStyle | undefined): string | undefined {
    if (!layoutStyle) {
        return undefined;
    }

    try {
        // Função helper para remover campos undefined/null
        // IMPORTANTE: Campos numéricos com valor 0 devem ser mantidos
        // IMPORTANTE: Strings não vazias (incluindo base64) devem ser mantidas
        const cleanObject = (obj: any, keepZeros: boolean = false): any => {
            const cleaned: any = {};
            for (const key in obj) {
                const value = obj[key];
                // Manter valores numéricos 0 se keepZeros for true
                if (keepZeros && typeof value === 'number' && value === 0) {
                    cleaned[key] = value;
                } else if (value !== undefined && value !== null) {
                    // Manter strings não vazias (especialmente base64 de imagens)
                    if (typeof value === 'string' && value.length > 0) {
                        cleaned[key] = value;
                    } else if (typeof value !== 'string') {
                        cleaned[key] = value;
                    }
                    // Se for string vazia, não incluir (mas isso não deve acontecer com base64)
                }
            }
            return cleaned;
        };

        // Função helper para limpar campos de imagem de fundo.
        // Para panfleto, preservar controles visuais (opacidade/blur/visibilidade/vignette)
        // mesmo quando a imagem vem de endpoint externo (sem backgroundImage no ds_configuracoes).
        const cleanBackgroundImageFields = (container: any, preserveControlsWithoutImage: boolean): any => {
            const cleaned = { ...container };

            // Verificar se há uma imagem válida (string não vazia)
            const hasImage = cleaned.backgroundImage &&
                typeof cleaned.backgroundImage === 'string' &&
                cleaned.backgroundImage.trim().length > 0;

            // Se não houver backgroundImage válida, remover campos relacionados
            if (!hasImage) {
                delete cleaned.backgroundImage;
                if (!preserveControlsWithoutImage) {
                    delete cleaned.backgroundImageOpacity;
                    delete cleaned.backgroundImageBlur;
                    delete cleaned.backgroundImageVisible;
                } else {
                    cleaned.backgroundImageOpacity = cleaned.backgroundImageOpacity ?? 100;
                    cleaned.backgroundImageBlur = cleaned.backgroundImageBlur ?? 0;
                    cleaned.backgroundImageVisible = cleaned.backgroundImageVisible !== undefined
                        ? cleaned.backgroundImageVisible
                        : true;
                }
            } else {
                // Se houver imagem, sempre incluir todos os campos com valores padrão
                cleaned.backgroundImageOpacity = cleaned.backgroundImageOpacity ?? 100;
                cleaned.backgroundImageBlur = cleaned.backgroundImageBlur ?? 0;
                cleaned.backgroundImageVisible = cleaned.backgroundImageVisible !== undefined
                    ? cleaned.backgroundImageVisible
                    : true;
            }

            return cleaned;
        };

        // Função helper para obter valor ou padrão, preservando 0
        const getValue = (value: any, defaultValue: any) => {
            return value !== undefined ? value : defaultValue;
        };

        // IMPORTANTE: Sempre incluir TODOS os campos, mesmo quando undefined (usar valores padrão)
        // Isso garante que o JSON sempre tenha a estrutura completa
        const productName: any = {
            color: getValue(layoutStyle.productName.color, '#000000'),
            fontSize: getValue(layoutStyle.productName.fontSize, 16),
            fontFamily: getValue(layoutStyle.productName.fontFamily, 'Arial, sans-serif'),
            lineHeight: getValue(layoutStyle.productName.lineHeight, 1.2),
            letterSpacing: getValue(layoutStyle.productName.letterSpacing, 0),
            fontWeight: getValue(layoutStyle.productName.fontWeight, 400),
            verticalStretch: getValue(layoutStyle.productName.verticalStretch, 100),
            showCurrency: getValue(layoutStyle.productName.showCurrency, true),
        };

        const price: any = {
            color: getValue(layoutStyle.price.color, '#000000'),
            fontSize: getValue(layoutStyle.price.fontSize, 16),
            fontFamily: getValue(layoutStyle.price.fontFamily, 'Arial, sans-serif'),
            lineHeight: getValue(layoutStyle.price.lineHeight, 1.2),
            letterSpacing: getValue(layoutStyle.price.letterSpacing, 0),
            fontWeight: getValue(layoutStyle.price.fontWeight, 400),
            verticalStretch: getValue(layoutStyle.price.verticalStretch, 100),
            showCurrency: getValue(layoutStyle.price.showCurrency, true),
        };

        const unit: any = {
            color: getValue(layoutStyle.unit.color, '#000000'),
            fontSize: getValue(layoutStyle.unit.fontSize, 16),
            fontFamily: getValue(layoutStyle.unit.fontFamily, 'Arial, sans-serif'),
            lineHeight: getValue(layoutStyle.unit.lineHeight, 1.2),
            letterSpacing: getValue(layoutStyle.unit.letterSpacing, 0),
            fontWeight: getValue(layoutStyle.unit.fontWeight, 400),
            verticalStretch: getValue(layoutStyle.unit.verticalStretch, 100),
            verticalOffset: getValue(layoutStyle.unit.verticalOffset, 0),
            verticalAlignment: layoutStyle.unit.verticalAlignment || 'center',
        };

        // productContainer: sempre incluir todos os campos
        // Preservar valores 0, mas usar valores padrão apenas quando realmente undefined
        const productContainer: any = {
            backgroundColor: getValue(layoutStyle.productContainer.backgroundColor, '#ffffff'),
            useGradient: getValue(layoutStyle.productContainer.useGradient, false),
            gradientStart: getValue(layoutStyle.productContainer.gradientStart, '#ffffff'),
            gradientEnd: getValue(layoutStyle.productContainer.gradientEnd, '#f0f0f0'),
            borderRadius: getValue(layoutStyle.productContainer.borderRadius, 8),
            paddingTop: getValue(layoutStyle.productContainer.paddingTop, 16),
            paddingBottom: getValue(layoutStyle.productContainer.paddingBottom, 16),
            paddingLeft: getValue(layoutStyle.productContainer.paddingLeft, 16),
            paddingRight: getValue(layoutStyle.productContainer.paddingRight, 16),
            marginTop: getValue(layoutStyle.productContainer.marginTop, 0),
            marginBottom: getValue(layoutStyle.productContainer.marginBottom, 0),
            marginLeft: getValue(layoutStyle.productContainer.marginLeft, 0),
            marginRight: getValue(layoutStyle.productContainer.marginRight, 0),
            horizontalGap: getValue(layoutStyle.productContainer.horizontalGap, 8),
            verticalGap: getValue(layoutStyle.productContainer.verticalGap, 8),
            // Background image settings - passar o valor diretamente (pode ser string base64 ou undefined)
            backgroundImage: layoutStyle.productContainer.backgroundImage,
            backgroundImageOpacity: layoutStyle.productContainer.backgroundImageOpacity !== undefined
                ? layoutStyle.productContainer.backgroundImageOpacity
                : 100,
            backgroundImageBlur: layoutStyle.productContainer.backgroundImageBlur !== undefined
                ? layoutStyle.productContainer.backgroundImageBlur
                : 0,
            backgroundImageVisible: layoutStyle.productContainer.backgroundImageVisible !== undefined
                ? layoutStyle.productContainer.backgroundImageVisible
                : true,
            backgroundVignetteEnabled: layoutStyle.productContainer.backgroundVignetteEnabled === true,
            backgroundVignetteIntensity: layoutStyle.productContainer.backgroundVignetteIntensity ?? 35,
        };

        // IMPORTANTE: Sempre incluir TODOS os campos obrigatórios com valores padrão quando undefined
        // Primeiro limpar undefined/null (mas manter 0 e strings não vazias, especialmente base64)
        const productContainerCleaned = cleanObject(productContainer, true);

        // Depois limpar campos de imagem de fundo (incluir apenas se houver imagem válida)
        // IMPORTANTE: Se houver imagem, ela já está em productContainerCleaned.backgroundImage
        const productContainerFinal = cleanBackgroundImageFields(
            productContainerCleaned,
            layoutStyle.panfletoContent != null
        );

        const config: any = {
            productName: productName, // Já tem todos os campos com valores padrão
            price: price, // Já tem todos os campos com valores padrão
            unit: unit, // Já tem todos os campos com valores padrão
            // productContainer deve sempre incluir todos os campos, mesmo quando são 0
            productContainer: productContainerFinal
        };

        // Adicionar campos opcionais (description, category, validUntil) se existirem
        // IMPORTANTE: Se existirem, sempre incluir TODOS os campos, mesmo quando undefined
        if (layoutStyle.description) {
            const description: any = {
                color: getValue(layoutStyle.description.color, '#666666'),
                fontSize: getValue(layoutStyle.description.fontSize, 16),
                fontFamily: getValue(layoutStyle.description.fontFamily, 'Arial, sans-serif'),
                lineHeight: getValue(layoutStyle.description.lineHeight, 1.2),
                letterSpacing: getValue(layoutStyle.description.letterSpacing, 0),
                fontWeight: getValue(layoutStyle.description.fontWeight, 400),
            };
            config.description = description; // Sempre incluir todos os campos
        }

        if (layoutStyle.category) {
            const category: any = {
                color: getValue(layoutStyle.category.color, '#666666'),
                fontSize: getValue(layoutStyle.category.fontSize, 14),
                fontFamily: getValue(layoutStyle.category.fontFamily, 'Arial, sans-serif'),
                lineHeight: getValue(layoutStyle.category.lineHeight, 1.2),
                letterSpacing: getValue(layoutStyle.category.letterSpacing, 0),
                fontWeight: getValue(layoutStyle.category.fontWeight, 400),
            };
            config.category = category; // Sempre incluir todos os campos
        }

        if (layoutStyle.validUntil) {
            const validUntil: any = {
                color: getValue(layoutStyle.validUntil.color, '#cc0000'),
                fontSize: getValue(layoutStyle.validUntil.fontSize, 14),
                fontFamily: getValue(layoutStyle.validUntil.fontFamily, 'Arial, sans-serif'),
                lineHeight: getValue(layoutStyle.validUntil.lineHeight, 1.2),
                letterSpacing: getValue(layoutStyle.validUntil.letterSpacing, 0),
                fontWeight: getValue(layoutStyle.validUntil.fontWeight, 400),
            };
            config.validUntil = validUntil; // Sempre incluir todos os campos
        }

        // Incluir priceBox se definido (sempre com todos campos e defaults)
        if (layoutStyle.priceBox) {
            const priceBox: any = {
                enabled: getValue(layoutStyle.priceBox.enabled, false),
                backgroundColor: getValue(layoutStyle.priceBox.backgroundColor, '#ffffff'),
                borderColor: getValue(layoutStyle.priceBox.borderColor, '#000000'),
                borderWidth: getValue(layoutStyle.priceBox.borderWidth, 0),
                borderRadius: getValue(layoutStyle.priceBox.borderRadius, 8),
                paddingTop: getValue(layoutStyle.priceBox.paddingTop, 4),
                paddingRight: getValue(layoutStyle.priceBox.paddingRight, 8),
                paddingBottom: getValue(layoutStyle.priceBox.paddingBottom, 4),
                paddingLeft: getValue(layoutStyle.priceBox.paddingLeft, 8),
                marginTop: getValue(layoutStyle.priceBox.marginTop, 0),
                marginRight: getValue(layoutStyle.priceBox.marginRight, 0),
                marginBottom: getValue(layoutStyle.priceBox.marginBottom, 0),
                marginLeft: getValue(layoutStyle.priceBox.marginLeft, 0),
            };
            (config as any).priceBox = priceBox;
        }

        // Bloco do panfleto — chave `panfletoContent` com `backgroundColor` normalizado (mesmo padrão que colunas em productContainer)
        if (layoutStyle.panfletoContent != null) {
            (config as any).panfletoContent = mergePanfletoContent(layoutStyle.panfletoContent, {
                includeDefaults: false,
            });
        }

        const jsonString = JSON.stringify(config);

        return jsonString;
    } catch (error) {
        return undefined;
    }
}

/**
 * Lê o bloco panfleto do JSON de configurações (camelCase e PascalCase que a API pode devolver).
 * Exportado para SignalR/Display usar a mesma lógica que jsonToLayoutStyle.
 */
export function parsePanfletoContentFromApiConfig(config: any): PanfletoContentConfig | undefined {
    return readPanfletoContentFromConfigObject(config);
}

/**
 * Converte JSON string de configuracoes para LayoutStyle
 * OTIMIZADO: Usa cache em memória para evitar reprocessamento
 */
export function jsonToLayoutStyle(jsonString: string | undefined): LayoutStyle | undefined {
    if (!jsonString || jsonString.trim() === '') {
        return undefined;
    }

    // Verificar se é uma string literal "string" (valor padrão da API quando não há configurações)
    if (jsonString.trim() === 'string' || jsonString.trim() === '"string"') {
        return undefined;
    }

    const layoutFromJsonCacheKey = `${jsonString}\0${JSON_TO_LAYOUT_CACHE_BUST}`;
    const cached = JSON_TO_LAYOUT_CACHE.get(layoutFromJsonCacheKey);
    if (cached !== undefined) {
        return cached;
    }

    try {
        const config = JSON.parse(jsonString);

        // Validar estrutura básica
        if (!config.productName || !config.price || !config.unit || !config.productContainer) {
            return undefined;
        }

        const layoutStyle: LayoutStyle = {
            productName: {
                color: config.productName.color || '#000000',
                fontSize: config.productName.fontSize ?? 16,
                fontFamily: config.productName.fontFamily || 'Arial, sans-serif',
                lineHeight: config.productName.lineHeight ?? 1.2,
                letterSpacing: config.productName.letterSpacing ?? 0,
                fontWeight: config.productName.fontWeight ?? 400,
                verticalStretch: config.productName.verticalStretch ?? 100,
                showCurrency: config.productName.showCurrency !== undefined ? config.productName.showCurrency : true,
            },
            price: {
                color: config.price.color || '#000000',
                fontSize: config.price.fontSize ?? 16,
                fontFamily: config.price.fontFamily || 'Arial, sans-serif',
                lineHeight: config.price.lineHeight ?? 1.2,
                letterSpacing: config.price.letterSpacing ?? 0,
                fontWeight: config.price.fontWeight ?? 400,
                verticalStretch: config.price.verticalStretch ?? 100,
                showCurrency: config.price.showCurrency !== undefined ? config.price.showCurrency : true,
            },
            unit: {
                color: config.unit.color || '#000000',
                fontSize: config.unit.fontSize ?? 16,
                fontFamily: config.unit.fontFamily || 'Arial, sans-serif',
                lineHeight: config.unit.lineHeight ?? 1.2,
                letterSpacing: config.unit.letterSpacing ?? 0,
                fontWeight: config.unit.fontWeight ?? 400,
                verticalStretch: config.unit.verticalStretch ?? 100,
                verticalOffset: config.unit.verticalOffset ?? 0,
                verticalAlignment: config.unit.verticalAlignment || 'center',
            },
            description: config.description ? {
                color: config.description.color || '#666666',
                fontSize: config.description.fontSize ?? 16,
                fontFamily: config.description.fontFamily || 'Arial, sans-serif',
                lineHeight: config.description.lineHeight ?? 1.2,
                letterSpacing: config.description.letterSpacing ?? 0,
                fontWeight: config.description.fontWeight ?? 400,
            } : undefined,
            category: config.category ? {
                color: config.category.color || '#666666',
                fontSize: config.category.fontSize ?? 14,
                fontFamily: config.category.fontFamily || 'Arial, sans-serif',
                lineHeight: config.category.lineHeight ?? 1.2,
                letterSpacing: config.category.letterSpacing ?? 0,
                fontWeight: config.category.fontWeight ?? 400,
            } : undefined,
            validUntil: config.validUntil ? {
                color: config.validUntil.color || '#cc0000',
                fontSize: config.validUntil.fontSize ?? 14,
                fontFamily: config.validUntil.fontFamily || 'Arial, sans-serif',
                lineHeight: config.validUntil.lineHeight ?? 1.2,
                letterSpacing: config.validUntil.letterSpacing ?? 0,
                fontWeight: config.validUntil.fontWeight ?? 400,
            } : undefined,
            productContainer: {
                backgroundColor: config.productContainer.backgroundColor || '#ffffff',
                useGradient: config.productContainer.useGradient === true,
                gradientStart: config.productContainer.gradientStart || '#ffffff',
                gradientEnd: config.productContainer.gradientEnd || '#f0f0f0',
                borderRadius: config.productContainer.borderRadius ?? 8,
                paddingTop: config.productContainer.paddingTop ?? 16,
                paddingBottom: config.productContainer.paddingBottom ?? 16,
                paddingLeft: config.productContainer.paddingLeft ?? 16,
                paddingRight: config.productContainer.paddingRight ?? 16,
                marginTop: config.productContainer.marginTop ?? 0,
                marginBottom: config.productContainer.marginBottom ?? 0,
                marginLeft: config.productContainer.marginLeft ?? 0,
                marginRight: config.productContainer.marginRight ?? 0,
                horizontalGap: config.productContainer.horizontalGap ?? 8,
                verticalGap: config.productContainer.verticalGap ?? 8,
                // Background image settings
                backgroundImage: config.productContainer.backgroundImage || undefined,
                backgroundImageOpacity: config.productContainer.backgroundImageOpacity ?? 100,
                backgroundImageBlur: config.productContainer.backgroundImageBlur ?? 0,
                backgroundImageVisible: config.productContainer.backgroundImageVisible !== undefined
                    ? config.productContainer.backgroundImageVisible
                    : true,
                backgroundVignetteEnabled: config.productContainer.backgroundVignetteEnabled === true,
                backgroundVignetteIntensity: config.productContainer.backgroundVignetteIntensity ?? 35,
            }
        };

        // priceBox opcional
        if (config.priceBox) {
            (layoutStyle as any).priceBox = {
                enabled: config.priceBox.enabled ?? false,
                backgroundColor: config.priceBox.backgroundColor || '#ffffff',
                borderColor: config.priceBox.borderColor || '#000000',
                borderWidth: config.priceBox.borderWidth ?? 0,
                borderRadius: config.priceBox.borderRadius ?? 8,
                paddingTop: config.priceBox.paddingTop ?? 4,
                paddingRight: config.priceBox.paddingRight ?? 8,
                paddingBottom: config.priceBox.paddingBottom ?? 4,
                paddingLeft: config.priceBox.paddingLeft ?? 8,
                marginTop: config.priceBox.marginTop ?? 0,
                marginRight: config.priceBox.marginRight ?? 0,
                marginBottom: config.priceBox.marginBottom ?? 0,
                marginLeft: config.priceBox.marginLeft ?? 0,
            };
        }

        // Panfleto: merge com suporte a chaves alternativas da API (PanfletoContent / BackgroundColor)
        layoutStyle.panfletoContent = mergePanfletoContent(parsePanfletoContentFromApiConfig(config));

        // Armazenar no cache
        trimCache(JSON_TO_LAYOUT_CACHE);
        JSON_TO_LAYOUT_CACHE.set(layoutFromJsonCacheKey, layoutStyle);

        return layoutStyle;
    } catch (error) {
        return undefined;
    }
}

/**
 * Mesmo caminho que colunas: a string `configuracoes` vira `LayoutStyle` via {@link jsonToLayoutStyle},
 * e o bloco `panfletoContent` (ex. cor de fundo) é reforçado a partir do **mesmo** JSON — fonte única
 * em `apiTelaToLocalScreen`, TV, sequência e editor; o `PromoCard` só recebe `layoutStyle`.
 */
export function layoutStyleFromTelaConfiguracoes(
  configuracoes: string | undefined
): LayoutStyle | undefined {
  const base = jsonToLayoutStyle(configuracoes);
  if (!configuracoes || !String(configuracoes).trim()) {
    return base;
  }
  const t = String(configuracoes).trim();
  if (t === 'string' || t === '"string"') {
    return base;
  }
  let config: unknown;
  try {
    config = JSON.parse(t);
  } catch {
    return base;
  }
  const fromApi = readPanfletoContentFromConfigObject(config);
  if (!fromApi) {
    return base;
  }
  if (!base) {
    return base;
  }
  return {
    ...base,
    panfletoContent: mergePanfletoContentPreserving(base.panfletoContent, fromApi),
  } as LayoutStyle;
}

/**
 * Adiciona descrição ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param descricao Descrição a ser adicionada
 * @returns JSON string atualizado com descrição
 */
export function adicionarDescricaoAoJson(configuracoesJson: string | undefined, descricao: string | undefined): string | undefined {
    if (!descricao || descricao.trim() === '') {
        if (!configuracoesJson) return undefined;
        const parsed = parseConfigForPatch(configuracoesJson);
        if (!parsed.ok) return parsed.original;
        delete (parsed.config as Record<string, unknown>).descricao;
        return JSON.stringify(parsed.config);
    }

    if (isEmptyConfigJson(configuracoesJson)) {
        return JSON.stringify({ descricao: descricao.trim() });
    }

    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    parsed.config.descricao = descricao.trim();
    return JSON.stringify(parsed.config);
}

/**
 * Extrai descrição do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Descrição extraída ou undefined
 */
export function extrairDescricaoDoJson(configuracoesJson: string | undefined): string | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);
        return config.descricao && typeof config.descricao === 'string' ? config.descricao.trim() : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Adiciona tag ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param tag Tag a ser adicionada
 * @returns JSON string atualizado com tag
 */
export function adicionarTagAoJson(configuracoesJson: string | undefined, tag: string | undefined): string | undefined {
    if (!tag || tag.trim() === '') {
        if (!configuracoesJson) return undefined;
        const parsed = parseConfigForPatch(configuracoesJson);
        if (!parsed.ok) return parsed.original;
        delete (parsed.config as Record<string, unknown>).tag;
        return JSON.stringify(parsed.config);
    }

    if (isEmptyConfigJson(configuracoesJson)) {
        return JSON.stringify({ tag: tag.trim() });
    }

    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    parsed.config.tag = tag.trim();
    return JSON.stringify(parsed.config);
}

/**
 * Extrai tag do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Tag extraída ou undefined
 */
export function extrairTagDoJson(configuracoesJson: string | undefined): string | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);
        return config.tag && typeof config.tag === 'string' ? config.tag.trim() : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Extrai todas as tags únicas de uma lista de telas
 * @param telas Array de telas (TelaBanner)
 * @returns Array de tags únicas (sem duplicatas) em maiúsculas
 */
export function extrairTagsUnicasDasTelas(telas: any[]): string[] {
    const tagsSet = new Set<string>();

    for (const tela of telas) {
        if (tela.configuracoes) {
            const tag = extrairTagDoJson(tela.configuracoes);
            if (tag && tag.trim() !== '') {
                // Converter para maiúsculas para evitar duplicatas
                tagsSet.add(tag.trim().toUpperCase());
            }
        }
    }

    // Converter Set para Array e ordenar alfabeticamente
    return Array.from(tagsSet).sort();
}

/**
 * Adiciona posição (posicaoX e posicaoY) ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param posicaoX Posição X (horizontal) em pixels
 * @param posicaoY Posição Y (vertical) em pixels
 * @returns JSON string atualizado com posição
 */
export function adicionarPosicaoAoJson(
    configuracoesJson: string | undefined,
    posicaoX: number | undefined,
    posicaoY: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;

    if (posicaoX !== undefined && typeof posicaoX === 'number') {
        config.posicaoX = posicaoX;
    }

    if (posicaoY !== undefined && typeof posicaoY === 'number') {
        config.posicaoY = posicaoY;
    }

    return JSON.stringify(config);
}

/**
 * Extrai posição (posicaoX e posicaoY) do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Objeto com posicaoX e posicaoY ou undefined
 */
export function extrairPosicaoDoJson(configuracoesJson: string | undefined): { posicaoX?: number; posicaoY?: number } | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);
        const result: { posicaoX?: number; posicaoY?: number } = {};

        if (typeof config.posicaoX === 'number') {
            result.posicaoX = config.posicaoX;
        }

        if (typeof config.posicaoY === 'number') {
            result.posicaoY = config.posicaoY;
        }

        // Retornar undefined se não houver nenhuma posição
        if (result.posicaoX === undefined && result.posicaoY === undefined) {
            return undefined;
        }

        return result;
    } catch {
        return undefined;
    }
}

/**
 * Adiciona sequência ao JSON de configurações
 * Todas as configurações de sequência ficam agrupadas em um objeto "sequencia"
 * @param configuracoesJson JSON string de configurações existente
 * @param nomeSequencia Nome da sequência (será convertido para UPPERCASE)
 * @param descricaoSequencia Descrição da sequência (apenas visual)
 * @param ordem Ordem da tela na sequência
 * @param tempoDuracao Tempo de duração da tela na sequência (em segundos)
 * @returns JSON string atualizado com sequência
 */
export function adicionarSequenciaAoJson(
    configuracoesJson: string | undefined,
    nomeSequencia: string | undefined,
    descricaoSequencia: string | undefined,
    ordem?: number,
    tempoDuracao?: number
): string | undefined {
    // Se não há nome de sequência, não fazer nada (não remover todas as sequências)
    if (!nomeSequencia || nomeSequencia.trim() === '') {
        return configuracoesJson;
    }

    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }

    try {
        let config: any = normalizarSequenciasNoConfig(parsed.config);

        // Garantir que sequencias é um array
        if (!Array.isArray(config.sequencias)) {
            config.sequencias = [];
        }

        const nomeSequenciaUpper = nomeSequencia.trim().toUpperCase();

        // Buscar se já existe sequência com este nome
        const indexExistente = config.sequencias.findIndex((seq: any) =>
            seq && typeof seq === 'object' &&
            seq.nomesequencia &&
            typeof seq.nomesequencia === 'string' &&
            seq.nomesequencia.trim().toUpperCase() === nomeSequenciaUpper
        );

        // Criar objeto da sequência
        const sequenciaObj: any = {
            nomesequencia: nomeSequenciaUpper
        };

        if (descricaoSequencia && descricaoSequencia.trim() !== '') {
            sequenciaObj.descricaosequencia = descricaoSequencia.trim();
        }

        if (ordem !== undefined) {
            sequenciaObj.ordemnasequencia = ordem;
        }

        if (tempoDuracao !== undefined) {
            sequenciaObj.temponasequencia = tempoDuracao;
        }

        // Se já existe, atualizar; senão, adicionar
        if (indexExistente >= 0) {
            config.sequencias[indexExistente] = sequenciaObj;
        } else {
            config.sequencias.push(sequenciaObj);
        }

        // Remover objeto sequencia antigo se existir (compatibilidade)
        if (config.sequencia) {
            delete config.sequencia;
        }

        return JSON.stringify(config);
    } catch (error) {
        return configuracoesJson;
    }
}

/**
 * Preserva todas as sequências de um JSON de configurações em outro JSON
 * @param configuracoesJson JSON string de configurações de destino (onde as sequências serão adicionadas)
 * @param configuracoesOrigem JSON string de configurações de origem (de onde as sequências serão extraídas)
 * @returns JSON string atualizado com todas as sequências preservadas
 */
export function preservarTodasSequenciasDoJson(
    configuracoesJson: string | undefined,
    configuracoesOrigem: string | undefined
): string | undefined {
    if (!configuracoesOrigem || configuracoesOrigem.trim() === '' || configuracoesOrigem.trim() === 'string' || configuracoesOrigem.trim() === '"string"') {
        return configuracoesJson;
    }

    try {
        // Extrair todas as sequências do JSON de origem
        const todasSequencias = extrairTodasSequenciasDoJson(configuracoesOrigem);

        if (todasSequencias.length === 0) {
            return configuracoesJson;
        }

        // Adicionar cada sequência ao JSON de destino
        let resultado = configuracoesJson;
        for (const sequencia of todasSequencias) {
            resultado = adicionarSequenciaAoJson(
                resultado,
                sequencia.nomesequencia,
                sequencia.descricaosequencia,
                sequencia.ordemnasequencia,
                sequencia.temponasequencia
            );
        }

        return resultado;
    } catch (error) {
        console.warn('Erro ao preservar sequências do JSON:', error);
        return configuracoesJson;
    }
}

/**
 * Remove uma sequência específica do JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param nomeSequencia Nome da sequência a remover (será comparado em maiúsculas)
 * @returns JSON string atualizado sem a sequência removida
 */
export function removerSequenciaDoJson(
    configuracoesJson: string | undefined,
    nomeSequencia: string
): string | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return configuracoesJson;
    }

    try {
        const config = JSON.parse(configuracoesJson);
        const configNormalizado = normalizarSequenciasNoConfig(config);

        if (!Array.isArray(configNormalizado.sequencias)) {
            return configuracoesJson;
        }

        const nomeSequenciaUpper = nomeSequencia.trim().toUpperCase();

        // Remover sequência com este nome
        configNormalizado.sequencias = configNormalizado.sequencias.filter((seq: any) => {
            if (!seq || typeof seq !== 'object' || !seq.nomesequencia) return true;
            return seq.nomesequencia.trim().toUpperCase() !== nomeSequenciaUpper;
        });

        // Se não há mais sequências, remover array vazio
        if (configNormalizado.sequencias.length === 0) {
            delete configNormalizado.sequencias;
        }

        // Remover objeto sequencia antigo se existir (compatibilidade)
        if (configNormalizado.sequencia) {
            delete configNormalizado.sequencia;
        }

        return JSON.stringify(configNormalizado);
    } catch {
        return configuracoesJson;
    }
}

/**
 * Normaliza o JSON de configurações para sempre usar array de sequências (sequencias)
 * Converte estrutura antiga (sequencia singular) para nova (sequencias plural)
 * @param configObj Objeto de configurações parseado
 * @returns Objeto normalizado com sequencias como array
 */
function normalizarSequenciasNoConfig(configObj: any): any {
    // Se já tem sequencias como array, retornar como está
    if (Array.isArray(configObj.sequencias)) {
        return configObj;
    }

    // Se tem sequencia singular (estrutura antiga), converter para array
    if (configObj.sequencia && typeof configObj.sequencia === 'object' && !Array.isArray(configObj.sequencia)) {
        configObj.sequencias = [configObj.sequencia];
        delete configObj.sequencia;
    } else if (!configObj.sequencias) {
        // Se não tem nenhuma, inicializar array vazio
        configObj.sequencias = [];
    }

    return configObj;
}

/**
 * Extrai todas as sequências de uma tela do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Array de objetos de sequência com {nomesequencia, descricaosequencia, ordemnasequencia, temponasequencia}
 */
export function extrairTodasSequenciasDoJson(configuracoesJson: string | undefined): Array<{
    nomesequencia: string;
    descricaosequencia?: string;
    ordemnasequencia?: number;
    temponasequencia?: number;
}> {
    // Se não há configurações ou é string vazia/inválida, retornar array vazio
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return [];
    }

    try {
        const config = JSON.parse(configuracoesJson);

        // Se não é um objeto válido, retornar array vazio
        if (!config || typeof config !== 'object') {
            return [];
        }

        const configNormalizado = normalizarSequenciasNoConfig(config);

        if (!Array.isArray(configNormalizado.sequencias)) {
            return [];
        }

        return configNormalizado.sequencias.filter((seq: any) =>
            seq && typeof seq === 'object' && seq.nomesequencia && typeof seq.nomesequencia === 'string'
        ).map((seq: any) => ({
            nomesequencia: seq.nomesequencia.trim(),
            descricaosequencia: seq.descricaosequencia && typeof seq.descricaosequencia === 'string' ? seq.descricaosequencia.trim() : undefined,
            ordemnasequencia: seq.ordemnasequencia !== undefined && typeof seq.ordemnasequencia === 'number' ? seq.ordemnasequencia : undefined,
            temponasequencia: seq.temponasequencia !== undefined && typeof seq.temponasequencia === 'number' ? seq.temponasequencia : undefined
        }));
    } catch (error) {
        // Se falhar ao parsear, retornar array vazio
        console.warn('Erro ao extrair sequências do JSON:', error, 'JSON:', configuracoesJson);
        return [];
    }
}

/**
 * Busca uma sequência específica por nome no JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @param nomeSequencia Nome da sequência a buscar (será comparado em maiúsculas)
 * @returns Objeto da sequência encontrada ou undefined
 */
export function buscarSequenciaPorNomeNoJson(
    configuracoesJson: string | undefined,
    nomeSequencia: string
): { nomesequencia: string; descricaosequencia?: string; ordemnasequencia?: number; temponasequencia?: number } | undefined {
    const todasSequencias = extrairTodasSequenciasDoJson(configuracoesJson);
    const nomeUpper = nomeSequencia.trim().toUpperCase();

    return todasSequencias.find(seq => seq.nomesequencia.toUpperCase() === nomeUpper);
}

/**
 * Extrai nome da sequência do JSON de configurações (compatibilidade - retorna primeira sequência)
 * @param configuracoesJson JSON string de configurações
 * @returns Nome da primeira sequência extraído ou undefined
 */
export function extrairNomeSequenciaDoJson(configuracoesJson: string | undefined): string | undefined {
    const todasSequencias = extrairTodasSequenciasDoJson(configuracoesJson);
    return todasSequencias.length > 0 ? todasSequencias[0].nomesequencia : undefined;
}

/**
 * Extrai descrição da sequência do JSON de configurações (compatibilidade - retorna primeira sequência)
 * @param configuracoesJson JSON string de configurações
 * @returns Descrição da primeira sequência extraída ou undefined
 */
export function extrairDescricaoSequenciaDoJson(configuracoesJson: string | undefined): string | undefined {
    const todasSequencias = extrairTodasSequenciasDoJson(configuracoesJson);
    return todasSequencias.length > 0 ? todasSequencias[0].descricaosequencia : undefined;
}

/**
 * Extrai todas as sequências únicas de uma lista de telas
 * @param telas Array de telas (TelaBannerOtimizada)
 * @returns Array de nomes de sequências únicas (sem duplicatas) em maiúsculas
 */
export function extrairSequenciasUnicasDasTelas(telas: any[]): string[] {
    const sequenciasSet = new Set<string>();

    for (const tela of telas) {
        if (tela.configuracoes) {
            const todasSequencias = extrairTodasSequenciasDoJson(tela.configuracoes);
            for (const seq of todasSequencias) {
                if (seq.nomesequencia && seq.nomesequencia.trim() !== '') {
                    sequenciasSet.add(seq.nomesequencia.trim().toUpperCase());
                }
            }
        }
    }

    // Converter Set para Array e ordenar alfabeticamente
    return Array.from(sequenciasSet).sort();
}

/**
 * Busca telas que pertencem a uma sequência específica
 * @param telas Array de telas (TelaBannerOtimizada)
 * @param nomeSequencia Nome da sequência (será comparado em maiúsculas)
 * @returns Array de telas que pertencem à sequência, ordenadas por ordemsequencia
 */
export function buscarTelasPorSequencia(telas: any[], nomeSequencia: string): any[] {
    const nomeSequenciaUpper = nomeSequencia.trim().toUpperCase();
    const telasSequencia = telas.filter(tela => {
        // Verificar se a tela tem configurações (mesmo que vazias, pode ter sequências)
        // IMPORTANTE: Não filtrar por configurações vazias - pode ter sequências salvas mesmo sem estilo
        if (tela.configuracoes === null || tela.configuracoes === undefined) {
            return false;
        }

        // Tentar buscar sequência mesmo se configurações estiverem vazias ou inválidas
        const sequenciaEncontrada = buscarSequenciaPorNomeNoJson(tela.configuracoes, nomeSequencia);
        return sequenciaEncontrada !== undefined;
    });

    // Ordenar por ordemsequencia se disponível
    return telasSequencia.sort((a, b) => {
        const seqA = buscarSequenciaPorNomeNoJson(a.configuracoes, nomeSequencia);
        const seqB = buscarSequenciaPorNomeNoJson(b.configuracoes, nomeSequencia);
        const ordemA = seqA?.ordemnasequencia ?? 999;
        const ordemB = seqB?.ordemnasequencia ?? 999;
        return ordemA - ordemB;
    });
}

/**
 * Extrai ordem da sequência do JSON de configurações (compatibilidade - retorna primeira sequência)
 * @param configuracoesJson JSON string de configurações
 * @returns Ordem da primeira sequência extraída ou undefined
 */
export function extrairOrdemSequenciaDoJson(configuracoesJson: string | undefined): number | undefined {
    const todasSequencias = extrairTodasSequenciasDoJson(configuracoesJson);
    return todasSequencias.length > 0 ? todasSequencias[0].ordemnasequencia : undefined;
}

/**
 * Extrai ordem de uma sequência específica por nome do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @param nomeSequencia Nome da sequência (será comparado em maiúsculas)
 * @returns Ordem da sequência extraída ou undefined
 */
export function extrairOrdemSequenciaPorNomeDoJson(configuracoesJson: string | undefined, nomeSequencia: string): number | undefined {
    const sequencia = buscarSequenciaPorNomeNoJson(configuracoesJson, nomeSequencia);
    return sequencia?.ordemnasequencia;
}

/**
 * Extrai duração da sequência do JSON de configurações (compatibilidade - retorna primeira sequência)
 * @param configuracoesJson JSON string de configurações
 * @returns Duração da primeira sequência extraída ou undefined
 */
export function extrairDuracaoSequenciaDoJson(configuracoesJson: string | undefined): number | undefined {
    const todasSequencias = extrairTodasSequenciasDoJson(configuracoesJson);
    return todasSequencias.length > 0 ? todasSequencias[0].temponasequencia : undefined;
}

/**
 * Extrai duração de uma sequência específica por nome do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @param nomeSequencia Nome da sequência (será comparado em maiúsculas)
 * @returns Duração da sequência extraída ou undefined
 */
export function extrairDuracaoSequenciaPorNomeDoJson(configuracoesJson: string | undefined, nomeSequencia: string): number | undefined {
    const sequencia = buscarSequenciaPorNomeNoJson(configuracoesJson, nomeSequencia);
    return sequencia?.temponasequencia;
}

/**
 * Extrai descrição de uma sequência específica por nome do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @param nomeSequencia Nome da sequência (será comparado em maiúsculas)
 * @returns Descrição da sequência extraída ou undefined
 */
export function extrairDescricaoSequenciaPorNomeDoJson(configuracoesJson: string | undefined, nomeSequencia: string): string | undefined {
    const sequencia = buscarSequenciaPorNomeNoJson(configuracoesJson, nomeSequencia);
    return sequencia?.descricaosequencia;
}

/**
 * Converte uma tela da API para o formato local
 * OTIMIZADO: Extração de produtoIds mais eficiente
 */
export function apiTelaToLocalScreen(telaBanner: TelaBanner): Partial<Screen> {
    // Extrair IDs dos produtos vinculados da API de forma otimizada
    let produtoIds: number[] = [];
    if (telaBanner.produtoIds && Array.isArray(telaBanner.produtoIds)) {
        // Pré-alocar array com tamanho aproximado para melhor performance
        produtoIds = new Array(telaBanner.produtoIds.length);
        let idx = 0;
        for (let i = 0; i < telaBanner.produtoIds.length; i++) {
            const produtoId = telaBanner.produtoIds[i]?.produtoId;
            if (typeof produtoId === 'number' && produtoId > 0) {
                produtoIds[idx++] = produtoId;
            }
        }
        // Ajustar tamanho do array se necessário
        if (idx < produtoIds.length) {
            produtoIds = produtoIds.slice(0, idx);
        }
    }

    // Converter configuracoes (JSON string) para layoutStyle — mesmo fluxo que colunas + reforço panfleto
    const layoutStyle = layoutStyleFromTelaConfiguracoes(telaBanner.configuracoes);

    // IMPORTANTE: Extrair descrição do JSON de configurações se não estiver no campo descricao
    let description = telaBanner.descricao || '';
    if (!description && telaBanner.configuracoes) {
        const descricaoDoJson = extrairDescricaoDoJson(telaBanner.configuracoes);
        if (descricaoDoJson) {
            description = descricaoDoJson;
        }
    }

    return {
        name: telaBanner.nome,
        description: description,
        isActive: telaBanner.ativo ?? false,
        layoutId: (telaBanner.tipo !== undefined ? TIPO_TO_LAYOUT[telaBanner.tipo] : 'simple2col') as LayoutType,
        // Produtos vinculados vêm da API
        promotions: produtoIds,
        backgroundColor: '#ffffff',
        backgroundImage: '',
        showHeader: true,
        duration: 30,
        // Layout style convertido das configurações
        layoutStyle: layoutStyle
    };
}

/**
 * Converte uma tela local para o formato da API (criação)
 */
export function localScreenToApiCreate(screen: Partial<Screen>): InserirTelaBannerCommand {
    // Converter layoutStyle para JSON string
    const configuracoes = layoutStyleToJson(screen.layoutStyle);

    return {
        nome: screen.name || 'Tela sem nome',
        tipo: screen.layoutId ? LAYOUT_TO_TIPO[screen.layoutId] : 0,
        configuracoes: configuracoes
        // NÃO incluir descricao - não existe na API
        // NÃO incluir ativo - não existe na API
    };
}

/**
 * Converte uma tela local para o formato da API (atualização)
 */
export function localScreenToApiUpdate(id: number, screen: Partial<Screen>): AlterarTelaBannerCommand {
    // Converter layoutStyle para JSON string
    const configuracoes = layoutStyleToJson(screen.layoutStyle);

    return {
        id,
        nome: screen.name,
        descricao: screen.description,
        tipo: screen.layoutId ? LAYOUT_TO_TIPO[screen.layoutId] : undefined,
        ativo: screen.isActive,
        configuracoes: configuracoes
    };
}

/**
 * Serviço de sincronização entre API e Store local
 */
class BannerSyncService {
    /**
     * Carrega telas da API e retorna no formato local
     */
    async carregarTelasAPI(): Promise<Partial<Screen>[]> {
        try {
            const resultado = await bannerService.listarTelas();
            return resultado.map(apiTelaToLocalScreen);
        } catch (error) {
            throw error;
        }
    }

    /**
     * Cria uma tela na API e retorna os dados
     */
    async criarTelaAPI(screen: Partial<Screen>): Promise<{ id?: number; telaId?: number; nome: string; ativo: boolean }> {
        try {
            const command = localScreenToApiCreate(screen);
            const resultado = await bannerService.criarTela(command);
            return resultado;
        } catch (error) {
            console.error('Erro ao criar tela na API:', error);
            throw error;
        }
    }

    /**
     * Atualiza uma tela na API.
     * Contrato do JSON:
     * - JSON fresco da API como base
     * - Se `layoutStyle` veio no save: overlay SÓ das chaves de estilo (demais intactas)
     * - Se não veio estilo: mantém `configuracoes` exatamente como na API
     * - `produtoIds`: só envia se `screen.promotions` foi informado (vínculo);
     *   omitir = backend não altera vínculos
     */
    async atualizarTelaAPI(apiId: number, screen: Partial<Screen>): Promise<boolean> {
        try {
            const telasOtimizadas = await bannerService.listarTelasOtimizado(apiId);
            const telaAtual = telasOtimizadas.find(t => t.id === apiId);

            if (!telaAtual) {
                throw new Error(`Tela com ID ${apiId} não encontrada na API`);
            }

            let configuracoesParaEnviar: string | undefined = undefined;
            if (screen.layoutStyle !== undefined) {
                // Estilo: overlay das chaves de estilo sobre o JSON existente (não rebuild do zero)
                configuracoesParaEnviar = aplicarEstiloSobreConfiguracoesExistentes(
                    telaAtual.configuracoes,
                    screen.layoutStyle
                );

                // Panfleto: se ainda não houver tempo por produto, grava default 10s (não sobrescreve se já existe)
                if (screen.layoutId === 'panfleto' && configuracoesParaEnviar) {
                    const tpf = extrairTempoPanfletoProdutoDoJson(configuracoesParaEnviar);
                    if (tpf === undefined || tpf <= 0) {
                        configuracoesParaEnviar = adicionarTempoPanfletoProdutoAoJson(
                            configuracoesParaEnviar,
                            10
                        );
                    }
                }

                if (!configuracoesParaEnviar && telaAtual.configuracoes) {
                    configuracoesParaEnviar = typeof telaAtual.configuracoes === 'string'
                        ? telaAtual.configuracoes
                        : JSON.stringify(telaAtual.configuracoes);
                }
            } else if (telaAtual.configuracoes) {
                // Configurações não foram modificadas — manter exatamente o JSON da API
                configuracoesParaEnviar = typeof telaAtual.configuracoes === 'string'
                    ? telaAtual.configuracoes
                    : JSON.stringify(telaAtual.configuracoes);
            }

            // produtoIds: só se o caller pediu alterar vínculos
            let produtoIdsParaEnviar: number[] | undefined = undefined;
            if (screen.promotions !== undefined && Array.isArray(screen.promotions)) {
                const produtoIdsFiltrados = screen.promotions
                    .filter(id => typeof id === 'number' && id > 0 && Number.isInteger(id));
                produtoIdsParaEnviar = produtoIdsFiltrados.length > 0 ? produtoIdsFiltrados : undefined;
            }

            const command: AlterarTelaBannerCommand = {
                id: apiId,
                nome: screen.name !== undefined ? screen.name : telaAtual.nome,
                tipo: screen.layoutId !== undefined ? LAYOUT_TO_TIPO[screen.layoutId] : (telaAtual.tipo ?? 0),
                produtoIds: produtoIdsParaEnviar,
                configuracoes: configuracoesParaEnviar,
            };

            const cleanedCommand: any = {
                id: command.id,
                nome: command.nome,
                tipo: command.tipo
            };

            if (command.produtoIds !== undefined) {
                cleanedCommand.produtoIds = command.produtoIds;
            }

            if (command.configuracoes !== undefined && command.configuracoes !== null) {
                cleanedCommand.configuracoes = command.configuracoes;
            }

            const sucesso = await bannerService.atualizarTela(cleanedCommand);
            return sucesso;
        } catch (error) {
            console.error('Erro ao atualizar tela na API:', error);
            throw error;
        }
    }

    /**
     * Exclui uma tela na API.
     * Para panfleto (tipo 1), a foto vinculada é removida antes da tela.
     */
    async excluirTelaAPI(apiId: number, tipo?: number): Promise<boolean> {
        try {
            const sucesso = await bannerService.deletarTela(apiId, tipo);
            return sucesso;
        } catch (error) {
            console.error('Erro ao excluir tela na API:', error);
            throw error;
        }
    }

    /**
     * Sincroniza uma tela local com a API
     * Retorna o ID da API se criado com sucesso
     */
    async sincronizarTela(screen: Partial<Screen>, apiId?: number): Promise<number | null> {
        try {
            if (apiId) {
                // Atualizar tela existente
                await this.atualizarTelaAPI(apiId, screen);
                return apiId;
            } else {
                // Criar nova tela
                const resultado = await this.criarTelaAPI(screen);
                return (resultado as any).telaId ?? resultado.id ?? null;
            }
        } catch (error) {
            console.error('Erro ao sincronizar tela:', error);
            return null;
        }
    }

    /**
     * Faz upload de vídeo para uma tela
     */
    async uploadVideoTela(apiId: number, videoFile: File): Promise<void> {
        try {
            await bannerService.uploadVideo(apiId, videoFile);
        } catch (error) {
            console.error('Erro ao fazer upload de vídeo:', error);
            throw error;
        }
    }

    /**
     * Obtém URL do vídeo de uma tela
     */
    getVideoUrl(apiId: number): string {
        return bannerService.getVideoUrl(apiId);
    }

    /**
     * Faz download do vídeo de uma tela
     */
    async downloadVideoTela(apiId: number): Promise<Blob> {
        try {
            return await bannerService.downloadVideo(apiId);
        } catch (error) {
            console.error('Erro ao fazer download de vídeo:', error);
            throw error;
        }
    }
}

export const bannerSyncService = new BannerSyncService();

/**
 * Adiciona configuração de paginação de produtos ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param paginacaoProdutos Quantidade de produtos por página
 * @returns JSON string atualizado com configuração de paginação
 */
export function adicionarPaginacaoAoJson(
    configuracoesJson: string | undefined,
    paginacaoProdutos: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;

    if (paginacaoProdutos !== undefined && typeof paginacaoProdutos === 'number' && paginacaoProdutos > 0) {
        config.paginacaoProdutos = paginacaoProdutos;
    } else if (paginacaoProdutos === 0) {
        delete config.paginacaoProdutos;
    }

    return JSON.stringify(config);
}

/**
 * Extrai configuração de paginação de produtos do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Quantidade de produtos por página ou undefined
 */
export function extrairPaginacaoDoJson(configuracoesJson: string | undefined): number | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);

        if (typeof config.paginacaoProdutos === 'number' && config.paginacaoProdutos > 0) {
            return config.paginacaoProdutos;
        }

        return undefined;
    } catch {
        return undefined;
    }
}

/**
 * Adiciona configuração de zoom ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param zoomLevel Nível de zoom (ex: 1.0, 1.2, etc)
 * @returns JSON string atualizado com configuração de zoom
 */
export function adicionarZoomAoJson(
    configuracoesJson: string | undefined,
    zoomLevel: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;

    if (zoomLevel !== undefined && typeof zoomLevel === 'number') {
        config.zoomLevel = zoomLevel;
    }

    return JSON.stringify(config);
}

/**
 * Extrai configuração de zoom do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Nível de zoom ou undefined
 */
export function extrairZoomDoJson(configuracoesJson: string | undefined): number | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);

        if (typeof config.zoomLevel === 'number') {
            return config.zoomLevel;
        }

        return undefined;
    } catch {
        return undefined;
    }
}

/**
 * Adiciona configuração de tempo de paginação ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param tempoPaginacao Tempo de paginação em segundos
 * @returns JSON string atualizado com configuração de tempo de paginação
 */
export function adicionarTempoPaginacaoAoJson(
    configuracoesJson: string | undefined,
    tempoPaginacao: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;

    const tempoNum = Number(tempoPaginacao);
    if (!isNaN(tempoNum) && tempoNum > 0) {
        config.tempoPaginacao = tempoNum;
    } else if (tempoPaginacao === 0 || tempoPaginacao === undefined) {
        delete config.tempoPaginacao;
    }

    return JSON.stringify(config);
}

/**
 * Extrai configuração de tempo de paginação do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Tempo de paginação em segundos ou undefined
 */
export function extrairTempoPaginacaoDoJson(configuracoesJson: string | undefined): number | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);

        // Tentar converter o valor para número, independente de como veio (string ou number)
        if (config.tempoPaginacao !== undefined && config.tempoPaginacao !== null) {
            const val = Number(config.tempoPaginacao);
            if (!isNaN(val) && val > 0) {
                return val;
            }
        }

        return undefined;
    } catch (e) {
        return undefined;
    }
}

/**
 * Tempo de exibição de cada produto no panfleto (segundos), salvo em `configuracoes` / ds_configuracoes.
 */
export function adicionarTempoPanfletoProdutoAoJson(
    configuracoesJson: string | undefined,
    segundos: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;
    const n = Number(segundos);
    if (!isNaN(n) && n > 0) {
        config.tempoPanfletoProduto = n;
    } else if (segundos === 0 || segundos === undefined) {
        delete config.tempoPanfletoProduto;
    }
    return JSON.stringify(config);
}

export function extrairTempoPanfletoProdutoDoJson(configuracoesJson: string | undefined): number | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }
    try {
        const config = JSON.parse(configuracoesJson);
        const raw = config.tempoPanfletoProduto ?? config.TempoPanfletoProduto;
        if (raw !== undefined && raw !== null) {
            const val = Number(raw);
            if (!isNaN(val) && val > 0) {
                return val;
            }
        }
        return undefined;
    } catch {
        return undefined;
    }
}

/**
 * Adiciona configuração de número de colunas ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param numeroColunas Número de colunas (1, 2 ou 3)
 * @returns JSON string atualizado com configuração de número de colunas
 */
export function adicionarNumeroColunasAoJson(
    configuracoesJson: string | undefined,
    numeroColunas: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;

    if (numeroColunas !== undefined && typeof numeroColunas === 'number' && numeroColunas >= 1 && numeroColunas <= 3) {
        config.numeroColunas = numeroColunas;
    }

    return JSON.stringify(config);
}

/**
 * Extrai configuração de número de colunas do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Número de colunas (1, 2 ou 3) ou undefined (padrão será 2)
 */
export function extrairNumeroColunasDoJson(configuracoesJson: string | undefined): number | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);

        if (typeof config.numeroColunas === 'number' && config.numeroColunas >= 1 && config.numeroColunas <= 3) {
            return config.numeroColunas;
        }

        return undefined;
    } catch {
        return undefined;
    }
}

/**
 * Adiciona configuração de largura da coluna (para 1 coluna) ao JSON de configurações
 * @param configuracoesJson JSON string de configurações existente
 * @param larguraColuna Largura da coluna em porcentagem (0-100)
 * @returns JSON string atualizado com configuração de largura da coluna
 */
export function adicionarLarguraColunaAoJson(
    configuracoesJson: string | undefined,
    larguraColuna: number | undefined
): string | undefined {
    const parsed = parseConfigForPatch(configuracoesJson);
    if (!parsed.ok) {
        return parsed.original;
    }
    const config = parsed.config as Record<string, unknown>;

    if (larguraColuna !== undefined && typeof larguraColuna === 'number' && larguraColuna >= 0 && larguraColuna <= 100) {
        config.larguraColuna = larguraColuna;
    }

    return JSON.stringify(config);
}

/**
 * Extrai configuração de largura da coluna (para 1 coluna) do JSON de configurações
 * @param configuracoesJson JSON string de configurações
 * @returns Largura da coluna em porcentagem (0-100) ou undefined (padrão será 100)
 */
export function extrairLarguraColunaDoJson(configuracoesJson: string | undefined): number | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return undefined;
    }

    try {
        const config = JSON.parse(configuracoesJson);

        if (typeof config.larguraColuna === 'number' && config.larguraColuna >= 0 && config.larguraColuna <= 100) {
            return config.larguraColuna;
        }

        return undefined;
    } catch {
        return undefined;
    }
}

/**
 * Remove a configuração de largura da coluna do JSON de configurações
 * Usado quando muda de 1 coluna para 2 ou 3 colunas
 * @param configuracoesJson JSON string de configurações
 * @returns JSON string sem a propriedade larguraColuna
 */
export function removerLarguraColunaDoJson(configuracoesJson: string | undefined): string | undefined {
    if (!configuracoesJson || configuracoesJson.trim() === '' || configuracoesJson.trim() === 'string' || configuracoesJson.trim() === '"string"') {
        return configuracoesJson;
    }

    try {
        const config = JSON.parse(configuracoesJson);
        
        // Remover a propriedade larguraColuna se existir
        if ('larguraColuna' in config) {
            delete config.larguraColuna;
        }

        return JSON.stringify(config);
    } catch (error) {
        // Se falhar ao processar, retornar JSON original
        return configuracoesJson;
    }
}